<template>
  <van-popup
    v-model="visible"
    :style="{
      width: '80%',
      maxWidth: '320px',
      borderRadius: '12px',
      padding: '40px 24px 32px',
      background: '#ffffff',
    }"
    :overlay="true"
    :close-on-click-overlay="closeOnClickOverlay"
    @click-overlay="handleOverlayClick"
  >
    <div class="status-popup">
      <!-- 状态图标 -->
      <div class="status-icon">
        <div
          :class="[
            'icon-circle',
            type === 'success' ? 'success-circle' : 'error-circle',
          ]"
        >
          <van-icon v-if="type === 'success'" name="success" class="icon" />
          <van-icon v-else name="fail" class="icon" />
        </div>
      </div>

      <!-- 标题 -->
      <div class="status-title">{{ title }}</div>

      <!-- 描述文字 -->
      <div class="status-description" v-if="description">
        {{ description }}
      </div>

      <!-- 传入自定义内容 -->
      <div class="custom-content" v-if="$slots.default">
        <slot></slot>
      </div>

      <!-- 按钮区域 -->
      <div class="button-area" v-if="showConfirmButton || showCancelButton">
        <van-button
          type="primary"
          :color="confirmButtonColor"
          round
          class="confirm-button"
          @click="handleConfirm"
        >
          {{ confirmText }}
        </van-button>
        <van-button
          type="default"
          round
          class="cancel-button"
          @click="handleCancel"
        >
          {{ cancelText }}
        </van-button>
      </div>
    </div>
  </van-popup>
</template>

<script>
import { Popup, Icon, Button } from "vant";

export default {
  name: "StatusPopup",
  components: {
    [Popup.name]: Popup,
    [Icon.name]: Icon,
    [Button.name]: Button,
  },
  props: {
    // 是否显示弹窗
    value: {
      type: Boolean,
      default: false,
    },
    // 弹窗类型：success | error
    type: {
      type: String,
      default: "success",
      validator: (value) => ["success", "error"].includes(value),
    },
    // 标题文字
    title: {
      type: String,
      required: true,
    },
    // 描述文字
    description: {
      type: String,
      default: "",
    },
    // 是否显示确认按钮
    showConfirmButton: {
      type: Boolean,
      default: false,
    },
    // 确认按钮文字
    confirmText: {
      type: String,
      default: "确定",
    },
    // 确认按钮颜色
    confirmButtonColor: {
      type: String,
      default: "#000000",
    },
    // 是否显示取消按钮
    showCancelButton: {
      type: Boolean,
      default: false,
    },
    // 取消按钮文字
    cancelText: {
      type: String,
      default: "取消",
    },
    // 自动关闭延迟时间（毫秒），0表示不自动关闭
    autoCloseDelay: {
      type: Number,
      default: 2000,
    },
    // 点击遮罩是否关闭
    closeOnClickOverlay: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      visible: false,
      autoCloseTimer: null,
    };
  },
  computed: {
    buttonColor() {
      return this.type === "success" ? "#07c160" : "#ee0a24";
    },
  },
  watch: {
    value: {
      handler(newVal) {
        this.visible = newVal;
        if (newVal) {
          this.startAutoCloseTimer();
        } else {
          this.clearAutoCloseTimer();
        }
      },
      immediate: true,
    },
    visible(newVal) {
      if (!newVal) {
        this.$emit("input", false);
        this.$emit("close");
        this.clearAutoCloseTimer();
      }
    },
  },
  beforeDestroy() {
    this.clearAutoCloseTimer();
  },
  methods: {
    // 开始自动关闭计时器
    startAutoCloseTimer() {
      if (
        this.autoCloseDelay > 0 &&
        !this.showConfirmButton &&
        !this.showCancelButton
      ) {
        this.autoCloseTimer = setTimeout(() => {
          this.close();
        }, this.autoCloseDelay);
      }
    },
    // 清除自动关闭计时器
    clearAutoCloseTimer() {
      if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer);
        this.autoCloseTimer = null;
      }
    },
    // 关闭弹窗
    close() {
      this.visible = false;
    },
    // 处理确认按钮点击
    handleConfirm() {
      this.$emit("confirm");
      this.close();
    },
    // 处理取消按钮点击
    handleCancel() {
      this.$emit("cancel");
      this.close();
    },
    // 处理遮罩点击
    handleOverlayClick() {
      if (this.closeOnClickOverlay) {
        this.close();
      }
    },
  },
};
</script>

<style lang="less" scoped>
.status-popup {
  text-align: center;
}

.status-icon {
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
}

.icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &.success-circle {
    background-color: #07c160;

    .icon {
      color: white;
      font-size: 32px;
    }
  }

  &.error-circle {
    background-color: #969799;

    .icon {
      color: white;
      font-size: 32px;
    }
  }
}

.status-title {
  font-size: 18px;
  font-weight: 600;
  color: #323233;
  line-height: 1.4;
}

.status-description {
  font-size: 14px;
  color: #969799;
  line-height: 1.5;
  margin: 12px 10px 0;
}

.custom-content {
  width: 100%;
  margin-top: 16px;
  padding: 0 10px;
  box-sizing: border-box;
}

.button-area {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .cancel-button {
    border: none;
    color: #86909C;
  }
}

.single-button {
  height: 44px;
  font-size: 16px;
  font-weight: 500;
}
</style>
